import { createContext, useContext, useMemo } from "react";

import { useClinics } from "@/hooks/useClinics";
import { OrganizationPractitioner, useOrganizationPractitioners } from "@/hooks/useOrganizationPractitioners";
import { usePractitionerOrganization } from "@/hooks/usePractitionerOrganization";
import { ClinicOption } from "@/types/clinic-types";
import { FhirResourceType, extractFhirResourceId } from "@/utils/fhir-utils";

import type { fhirR4 } from "@smile-cdr/fhirts";

interface OrganizationPractitionersContextValue {
  organizationReference: string | undefined;
  /** Active practitioners only — use for dropdowns and selectors. */
  practitioners: OrganizationPractitioner[];
  practitionerRoles: fhirR4.PractitionerRole[];
  practitionerResources: fhirR4.Practitioner[];
  /** All practitioners including inactive — use for lists/tabs that show inactive members. */
  allPractitioners: OrganizationPractitioner[];
  allPractitionerRoles: fhirR4.PractitionerRole[];
  allPractitionerResources: fhirR4.Practitioner[];
  clinics: ClinicOption[];
  isLoading: boolean;
  refetch: () => Promise<unknown>;
}

const noop = () => Promise.resolve();

const OrganizationPractitionersContext = createContext<OrganizationPractitionersContextValue>({
  organizationReference: undefined,
  practitioners: [],
  practitionerRoles: [],
  practitionerResources: [],
  allPractitioners: [],
  allPractitionerRoles: [],
  allPractitionerResources: [],
  clinics: [],
  isLoading: true,
  refetch: noop,
});

export const OrganizationPractitionersProvider = ({ children }: { children: React.ReactNode }) => {
  const { organizationReference } = usePractitionerOrganization();
  const {
    practitioners: allPractitioners,
    practitionerRoles: allPractitionerRoles,
    practitionerResources: allPractitionerResources,
    isLoading: isLoadingPractitioners,
    refetch,
  } = useOrganizationPractitioners(organizationReference);

  // Filter out inactive practitioners for selections (only include explicitly active)
  const { practitioners, practitionerRoles, practitionerResources } = useMemo(() => {
    const activeIds = new Set(allPractitionerResources.filter(p => p.active === true).map(p => p.id));

    return {
      practitioners: allPractitioners.filter(p => activeIds.has(p.id)),
      practitionerRoles: allPractitionerRoles.filter(r => {
        const pid = extractFhirResourceId(r.practitioner?.reference, FhirResourceType.Practitioner);
        return r.active === true && pid && activeIds.has(pid);
      }),
      practitionerResources: allPractitionerResources.filter(p => activeIds.has(p.id)),
    };
  }, [allPractitioners, allPractitionerRoles, allPractitionerResources]);

  const parentOrgId = extractFhirResourceId(organizationReference, FhirResourceType.Organization);
  const { clinics, isLoading: isLoadingClinics } = useClinics(parentOrgId, {
    includeParentOrg: true,
    fetchAllPages: true,
  });

  const isLoading = isLoadingPractitioners || isLoadingClinics;

  return (
    <OrganizationPractitionersContext.Provider
      value={{
        organizationReference,
        practitioners,
        practitionerRoles,
        practitionerResources,
        allPractitioners,
        allPractitionerRoles,
        allPractitionerResources,
        clinics,
        isLoading,
        refetch,
      }}
    >
      {children}
    </OrganizationPractitionersContext.Provider>
  );
};

export const useOrganizationPractitionersContext = (): OrganizationPractitionersContextValue =>
  useContext(OrganizationPractitionersContext);
